/**
 * עגלת הבקשות — TASKS 4.3 · PRD 7.4 (טבלת עגלה).
 *
 * ⛔ ⛔ **אין מחיר ואין סכום בשום מקום בעמוד הזה** (6.5) — רק המידות שהלקוח
 *    הזין. עריכה משתמשת ב-`PricingFields` (4.2) עם `initialMeasurements`,
 *    אותה ולידציה בדיוק כמו בדף המוצר.
 *
 * ⭐ עריכה מחייבת מיד — `onChange` מעדכן את `cart` רק כשהערך **תקין**; קלט
 *    ביניים לא-תקין ⛔ אינו נמחק אלא פשוט לא מחליף את הערך התקין האחרון בעגלה.
 */
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { ShoppingCart } from '@phosphor-icons/react';
// ⚠️ ⚠️ תת-נתיב, ⛔ לא הבארל `@noam/shared` — הבארל מייצא גם `db/migrationRunner.ts`
//    (mysql2) ו-`pdf/textMeasure.ts` (fontkit), ששניהם קורסים ב-Vite dev
//    (mysql2 מנסה להרחיב מחלקות Node כמו EventEmitter/Readable שהודחו
//    ב-דפדפן — "TypeError: Cannot read properties of undefined (reading
//    'prototype')", עמוד ריק לגמרי). נתפס ב-Playwright אמיתי, TASKS 4.3.
import { formatQuantityLabel } from '@noam/shared/pricing';
import { useCart } from '../cart/CartContext.js';
import { PricingFields, type PricingFieldsResult } from '../product/PricingFields.js';
import { EmptyState } from '../components/EmptyState.js';

export function CartPage() {
  const cart = useCart();
  const navigate = useNavigate();

  if (cart.items.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">עגלת הבקשות ריקה</h1>
        <EmptyState
          icon={ShoppingCart}
          message="עוד לא הוספתם מוצרים לעגלה."
          action={
            <Link to="/catalog" className="btn btn-cta">
              לצפייה בקטלוג
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <h1 className="text-3xl font-bold">עגלת הבקשות</h1>
      <p className="text-muted mt-2 max-w-2xl">
        אלה הפריטים שבחרתם. אין כאן מחירים - הצעת מחיר מלאה תגיע אליכם בדוא"ל אחרי מילוי הפרטים
        בעמוד הבא.
      </p>

      {/* ⭐ DESIGN.md 5.5 — "כרטיס סיכום דביק בדסקטופ (עמודה שנייה)". */}
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_18rem]">
        <ul className="space-y-6">
          {/* ⭐ TASKS 6.2 · DESIGN.md 6.2 — הסרת פריט: `exit={{opacity:0, height:0}}`,
              250ms. `AnimatePresence` דורש שהילדים הישירים יישאו `key` (יש כבר,
              `item.lineId`). */}
          <AnimatePresence initial={false}>
            {cart.items.map((item) => (
              <motion.li
                key={item.lineId}
                layout
                initial={false}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden rounded-lg border border-line p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {/* ⭐ DESIGN.md 5.5 — "תמונה קטנה" בכרטיס העגלה. */}
                    <div className="bg-canvas flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-line p-1">
                      {item.productImage ? (
                        <img
                          src={item.productImage.url}
                          alt={item.productImage.alt}
                          loading="lazy"
                          width={64}
                          height={64}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <span className="sr-only">אין תמונה זמינה</span>
                      )}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">
                        {item.productName}
                        {item.variantLabel ? ` - ${item.variantLabel}` : ''}
                      </h2>
                      <p className="ltr-inline text-muted text-sm">{item.sku}</p>
                      <p className="mt-1 text-sm font-semibold">
                        {formatQuantityLabel(item.pricingType, item.measurements, item.unitLabel)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => cart.removeItem(item.lineId)}
                    className="btn btn-secondary"
                    aria-label={`הסרת ${item.productName} מהעגלה`}
                  >
                    הסרה
                  </button>
                </div>

                <div className="mt-4 border-t border-line pt-4">
                  <PricingFields
                    pricingType={item.pricingType}
                    unitLabel={item.unitLabel}
                    minOrderQty={item.minOrderQty}
                    qtyStep={item.qtyStep}
                    initialMeasurements={item.measurements}
                    singleQuantityField={item.sku === 'MG-0001'}
                    onChange={(result: PricingFieldsResult) => {
                      if (result.isValid)
                        cart.updateItemMeasurements(item.lineId, result.measurements);
                    }}
                  />
                </div>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>

        {/* ⭐ DESIGN.md 5.5 — "מספר פריטים + CTA. בלי סכומים." יושב מתחת
            ל-Header הדביק + מרווח 12px. ⭐ TASKS 6.7 (27.08.2026) — נגזר
            מ-`--header-h` (`index.css`) במקום ערך `136px` קבוע, כדי שיישאר
            מסונכרן אוטומטית עם `Header.tsx` בכל שכבת viewport (כולל שכבת
            הזום הנמוך החדשה), ⛔ בלי עדכון ידני נפרד בכל שינוי גובה הדר. */}
        <div className="h-fit rounded-lg border border-line bg-surface p-5 lg:sticky lg:top-[calc(var(--header-h)+12px)]">
          <p className="font-bold">
            {cart.items.length} {cart.items.length === 1 ? 'פריט' : 'פריטים'} בעגלה
          </p>
          <button
            type="button"
            onClick={() => navigate('/request')}
            className="btn btn-cta mt-4 w-full"
          >
            המשך לבקשת הצעה
          </button>
          <button
            type="button"
            onClick={() => cart.clearCart()}
            className="btn btn-secondary mt-3 w-full"
          >
            ריקון העגלה
          </button>
        </div>
      </div>
    </div>
  );
}
